/**
 * usePricepointTypes Hook
 *
 * Fetches pricepoint types for a specific page from the API.
 * Returns types and sections formatted for ModuleNavSidebar.
 *
 * @example
 * const { types, sections, isLoading, error } = usePricepointTypes({ page: 'cost' })
 */

import { useQuery } from '@tanstack/react-query'
import { apiService } from '@/services/apiService'
import type { PricepointTypesResponse } from '@/types/pricepoint.types'

export type PricepointPage = 'cost' | 'price' | 'negotiate'

interface UsePricepointTypesParams {
  page: PricepointPage
}

/**
 * Fetch pricepoint types for a specific page
 *
 * @param page - The page to fetch types for (cost, price, negotiate)
 * @returns Query result with types and sections
 */
export function usePricepointTypes({ page }: UsePricepointTypesParams) {
  return useQuery({
    queryKey: ['pricepoint-types', page],
    queryFn: async () => {
      const response = await apiService.get<PricepointTypesResponse>(
        `/pricepoint-types?page=${page}`
      )

      if (!response.success) {
        throw new Error(response.errors?.[0]?.message || 'Failed to fetch pricepoint types')
      }

      return response.data!
    },
    staleTime: 1000 * 60 * 60, // 1 hour - types rarely change
    gcTime: 1000 * 60 * 60 * 2, // 2 hours cache
  })
}
